import React, { useRef, useState, useEffect } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

export default function Knob({ currentAngle, onAngleChange, onSnap }) {
  const knobRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Motion values for smooth rotation
  const rotation = useMotionValue(currentAngle);
  const smoothRotation = useSpring(rotation, { stiffness: 60, damping: 15 });

  // Update motion value when prop changes (from external clicks)
  useEffect(() => {
    if (!isDragging) {
      rotation.set(currentAngle);
    }
  }, [currentAngle, isDragging, rotation]);

  const handlePointerDown = (e) => {
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging || !knobRef.current) return;

    const rect = knobRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Calculate angle from center to pointer
    const angleRad = Math.atan2(e.clientY - centerY, e.clientX - centerX);
    let angleDeg = angleRad * (180 / Math.PI) + 90; // Adjust so top is 0

    // Normalize angle for the UI
    if (angleDeg > 180) angleDeg -= 360;

    rotation.set(angleDeg);
    onAngleChange(angleDeg);
  };

  const handlePointerUp = (e) => {
    setIsDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
    onSnap(rotation.get());
  };

  return (
    <div className="relative w-56 h-56 md:w-72 md:h-72 flex-shrink-0 flex items-center justify-center rounded-full shadow-[0_15px_40px_rgba(0,0,0,0.6),_inset_0_2px_5px_rgba(255,255,255,0.7)] bg-gradient-to-br from-gray-400 via-gray-300 to-gray-500 p-2 md:p-4">
      {/* Outer Bezel (Dark ring) */}
      <div className="w-full h-full rounded-full bg-gradient-to-b from-gray-800 to-gray-900 p-1 md:p-[8px] shadow-[inset_0_4px_10px_rgba(0,0,0,0.8)] relative overflow-hidden">
        
        {/* Inner Rotating Dial */}
        <motion.div
          ref={knobRef}
          className="relative w-full h-full rounded-full shadow-[0_5px_15px_rgba(0,0,0,0.6)] cursor-grab active:cursor-grabbing touch-none"
          style={{
            background: 'conic-gradient(from 180deg at 50% 50%, #d1d5db 0deg, #f3f4f6 45deg, #9ca3af 90deg, #f3f4f6 135deg, #d1d5db 180deg, #f3f4f6 225deg, #9ca3af 270deg, #f3f4f6 315deg, #d1d5db 360deg)',
            rotate: smoothRotation
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          {/* Top highlight for 3D effect */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-b from-white/40 to-transparent pointer-events-none" />
          
          {/* Inset center circle */}
          <div className="absolute inset-5 md:inset-8 rounded-full shadow-[inset_0_2px_10px_rgba(0,0,0,0.3)] bg-gradient-to-br from-gray-100 to-gray-300">
            <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,transparent_40%,rgba(0,0,0,0.05)_100%)]" />
            
            {/* Grip circles for realism */}
            <div className="absolute inset-4 border border-gray-400/20 rounded-full" />
            <div className="absolute inset-8 border border-gray-400/10 rounded-full" />
          </div>

          {/* Indicator Notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-8 md:w-5 md:h-10 pt-1">
            <div className="w-full h-full bg-gray-800 rounded-sm shadow-[inset_0_1px_4px_rgba(0,0,0,0.8),_0_1px_1px_rgba(255,255,255,0.5)] flex justify-center">
              <div className="w-1 h-4 md:h-6 bg-red-500 mt-1 rounded-full shadow-[0_0_8px_rgba(239,68,68,1)]" />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}